'use client';

import { c as _c } from "react/compiler-runtime";
import { PerformanceMonitor } from '@react-three/drei';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Vector2 } from 'three';
import { setConsoleFunction, WebGPURenderer } from 'three/webgpu';
import { markWebGPUFatal } from '@/app/hooks/useWebGPUFallbackMode';
import { DEFAULT_CAMERA_FOV, DEFAULT_CAMERA_POSITION } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic/configs';
import type { SpartanWebGPUCanvasBasicProps } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic/types';
import { ShaderPrecompiler } from '@/app/lcv/components/sections/spartan/shared/webgpu/shader-pre-compiler';
import { reportRumWarning } from '@/app/utils/reportRum';
import { safeCall } from '@/app/utils/safeCall';
import logger from '@/lib/logger';

// react-three-fiber's root store unconditionally constructs a
// `THREE.Clock` on <Canvas> mount, which three.js (r183+) now logs as
// deprecated in favor of THREE.Timer. R3F has no `clock` override prop
// and hasn't migrated (checked latest + canary), so this can't be fixed
// from our side -- filter just this one message rather than the
// blanket-override `console.warn` would require.
setConsoleFunction((type, message, ...params) => {
  if (type === 'warn' && typeof message === 'string' && message.includes('Clock: This module has been deprecated')) {
    return;
  }
  // eslint-disable-next-line no-console
  console[type](message, ...params);
});

// See the note where dpr is initialised.
const MAX_DPR = 1.5;
const MIN_DPR = 1;
const STALL_CHECK_MS = 2000;

// HOW MANY CONSECUTIVE STALLED CHECKS BEFORE THE CANVAS IS DECLARED DEAD.
//
// Two, not one: a single missed window is a hitch -- a long compile, a
// garbage collection, a device waking up -- and tearing the scene down for
// one of those would be worse than the hitch. Four seconds of a visible
// canvas drawing nothing is not a hitch.
const STALL_STRIKES_BEFORE_FATAL = 2;

// HOW LONG THE RENDERER HAS TO COME UP AT ALL.
//
// This covers the one failure the watchdog below CANNOT see. R3F awaits
// the `gl` factory before it mounts any children, so if `renderer.init()`
// never settles -- an adapter request that hangs rather than rejecting --
// the watchdog is never mounted to notice. Nothing throws, nothing logs,
// and the canvas is simply blank forever.
//
// Generous, because a cold GPU driver on a slow device is genuinely slow
// and a false positive costs a real scene.
const RENDERER_READY_TIMEOUT_MS = 10000;
const RenderWatchdog = t0 => {
  const $ = _c(15);
  const {
    onFatalError,
    paused: t1
  } = t0;
  const paused = t1 === undefined ? false : t1;
  const framesRef = useRef(0);
  const fatalRef = useRef(onFatalError);
  let t2;
  if ($[0] !== onFatalError) {
    t2 = () => {
      fatalRef.current = onFatalError;
    };
    $[0] = onFatalError;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useEffect(t2);
  const pausedRef = useRef(paused);
  const resumedAtRef = useRef(0);
  let t3;
  let t4;
  if ($[2] !== paused) {
    t3 = () => {
      pausedRef.current = paused;
      if (!paused) {
        resumedAtRef.current = performance.now();
      }
    };
    t4 = [paused];
    $[2] = paused;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  const gl = useThree(_temp);
  const size = useThree(_temp2);
  let t5;
  if ($[5] !== gl || $[6] !== size) {
    t5 = {
      gl,
      size
    };
    $[5] = gl;
    $[6] = size;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const nudgeRef = useRef(t5);
  let t6;
  let t7;
  if ($[8] !== gl || $[9] !== size) {
    t6 = () => {
      nudgeRef.current = {
        gl,
        size
      };
    };
    t7 = [gl, size];
    $[8] = gl;
    $[9] = size;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      framesRef.current = framesRef.current + 1;
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  useFrame(t8);
  let t10;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      let lastSeen = -1;
      let hasReported = false;
      let hasWarned = false;
      let strikes = 0;
      let hasNudged = false;
      let hasEscalated = false;
      let probeFrames = 0;
      let probeRaf = 0;
      const probe = () => {
        probeFrames = probeFrames + 1;
        probeFrames;
        probeRaf = requestAnimationFrame(probe);
      };
      probeRaf = requestAnimationFrame(probe);
      let lastCheckAt = performance.now();
      let lastProbe = 0;
      const resetWindow = () => {
        lastSeen = framesRef.current;
        hasReported = false;
        hasWarned = false;
        strikes = 0;
      };
      const check = () => {
        const now = performance.now();
        const elapsedMs = now - lastCheckAt;
        const probeDelta = probeFrames - lastProbe;
        lastCheckAt = now;
        lastProbe = probeFrames;
        if (document.visibilityState !== "visible") {
          resetWindow();
          return;
        }
        if (pausedRef.current || now - resumedAtRef.current < STALL_CHECK_MS) {
          resetWindow();
          return;
        }
        const frames = framesRef.current;
        if (frames !== lastSeen) {
          resetWindow();
          return;
        }
        const diagnosis = {
          frames,
          elapsedMs: Math.round(elapsedMs),
          probeFrames: probeDelta,
          sincePageLoadMs: Math.round(now),
          hasFocus: document.hasFocus()
        };
        const wasMainThreadBlocked = elapsedMs > STALL_CHECK_MS * 1.5;
        const wasBrowserStarved = probeDelta === 0;
        if (wasMainThreadBlocked || wasBrowserStarved) {
          strikes = 0;
          if (!hasWarned) {
            hasWarned = true;
            logger.warn(wasMainThreadBlocked ? "[webgpu] no frames: main thread blocked (ignored)" : "[webgpu] no frames: browser sent none (ignored)", diagnosis);
          }
          return;
        }
        strikes = strikes + 1;
        strikes;
        if (!hasReported) {
          hasReported = true;
          logger.warn("[webgpu] render loop stalled, nudging", diagnosis);
        }
        if (!hasNudged) {
          hasNudged = true;
          const {
            gl: renderer,
            size: canvasSize
          } = nudgeRef.current;
          if (renderer && canvasSize.width > 0 && canvasSize.height > 0) {
            renderer.setSize(canvasSize.width, canvasSize.height, false);
          }
          return;
        }
        if (!hasEscalated && strikes >= STALL_STRIKES_BEFORE_FATAL) {
          hasEscalated = true;
          logger.warn("[webgpu] render stall, fallback", diagnosis);
          reportRumWarning("webgpu_render_stall", diagnosis);
          safeCall(fatalRef.current);
        }
      };
      const timer = window.setInterval(check, STALL_CHECK_MS);
      return () => {
        window.clearInterval(timer);
        cancelAnimationFrame(probeRaf);
      };
    };
    t10 = [];
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useEffect(t9, t10);
  return null;
};
const RendererSizeSync = () => {
  const $ = _c(6);
  const gl = useThree(_temp3);
  const size = useThree(_temp4);
  const invalidate = useThree(_temp5);
  const hasForcedRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== gl || $[1] !== invalidate || $[2] !== size.height || $[3] !== size.width) {
    t0 = () => {
      if (!gl || size.width <= 0 || size.height <= 0) {
        return;
      }
      const current = gl.getSize(new Vector2());
      const isAlreadySized = current.width === size.width && current.height === size.height;
      if (hasForcedRef.current && isAlreadySized) {
        return;
      }
      hasForcedRef.current = true;
      gl.setSize(size.width, size.height, false);
      invalidate();
    };
    t1 = [gl, size.width, size.height, invalidate];
    $[0] = gl;
    $[1] = invalidate;
    $[2] = size.height;
    $[3] = size.width;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  return null;
};

// A short, flat string for the log context -- device-lost info and GPU
// errors are objects, and a full JSON dump is unreadable in a list.
const describeFailure = (info: unknown): string => {
  if (info instanceof Error) return info.message;
  if (info && typeof info === 'object' && 'message' in info) {
    return String((info as {
      message: unknown;
    }).message);
  }
  return String(info);
};
const createRenderer = async (canvas: HTMLCanvasElement, onFatalError?: () => void, onRendererReady?: () => void): Promise<WebGPURenderer> => {
  const renderer = new WebGPURenderer({
    canvas,
    antialias: true,
    alpha: true
  });
  try {
    await renderer.init();
  } catch (e) {
    const context = {
      message: describeFailure(e)
    };
    logger.warn('[webgpu] init failed, fallback', context);
    reportRumWarning('webgpu_init_failed', context);
    safeCall(onFatalError);
    throw e;
  }

  // Once per renderer: an uncaptured GPU error can repeat every frame
  // until the canvas is gone, and one entry says everything.
  let hasFailed = false;
  const fail = (action: string, label: string, info: unknown) => {
    if (hasFailed) return;
    hasFailed = true;
    const context = {
      message: describeFailure(info)
    };
    logger.warn(`[webgpu] ${label}, fallback`, context);
    reportRumWarning(action, context);
    safeCall(onFatalError);
  };
  renderer.onDeviceLost = info => fail('webgpu_device_lost', 'device lost', info);
  renderer.onError = info => fail('webgpu_gpu_error', 'GPU error', info);
  safeCall(onRendererReady);
  return renderer;
};
export const SpartanWebGPUCanvasBasic = t0 => {
  const $ = _c(36);
  const {
    cameraPosition: t1,
    cameraFov: t2,
    onFatalError,
    onRendererReady,
    readyToCompile,
    onShaderCompiled,
    onShaderCompileError,
    paused: t3,
    children
  } = t0;
  const cameraPosition = t1 === undefined ? DEFAULT_CAMERA_POSITION : t1;
  const cameraFov = t2 === undefined ? DEFAULT_CAMERA_FOV : t2;
  const paused = t3 === undefined ? false : t3;
  const [dpr, setDpr] = useState(MAX_DPR);
  let t4;
  let t5;
  if ($[0] !== paused) {
    t4 = () => {
      if (!paused) {
        setDpr(MAX_DPR);
      }
    };
    t5 = [paused];
    $[0] = paused;
    $[1] = t4;
    $[2] = t5;
  } else {
    t4 = $[1];
    t5 = $[2];
  }
  useEffect(t4, t5);
  const [isReadyToMount, setIsReadyToMount] = useState(false);
  let t6;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setIsReadyToMount(true);
    t7 = [];
    $[3] = t6;
    $[4] = t7;
  } else {
    t6 = $[3];
    t7 = $[4];
  }
  useEffect(t6, t7);
  const isRendererReadyRef = useRef(false);
  const fatalRef = useRef(onFatalError);
  let t8;
  if ($[5] !== onFatalError) {
    t8 = () => {
      fatalRef.current = onFatalError;
    };
    $[5] = onFatalError;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  useEffect(t8);
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      markWebGPUFatal();
      safeCall(fatalRef.current);
    };
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const handleFatal = t9;
  let t10;
  if ($[8] !== onRendererReady) {
    t10 = () => {
      isRendererReadyRef.current = true;
      safeCall(onRendererReady);
    };
    $[8] = onRendererReady;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const handleRendererReady = t10;
  let t11;
  let t12;
  if ($[10] !== isReadyToMount) {
    t11 = () => {
      if (!isReadyToMount) {
        return;
      }
      const timer = window.setTimeout(() => {
        if (isRendererReadyRef.current) {
          return;
        }
        const context = {
          timeoutMs: RENDERER_READY_TIMEOUT_MS
        };
        logger.warn("[webgpu] init timed out, fallback", context);
        reportRumWarning("webgpu_init_timeout", context);
        handleFatal();
      }, RENDERER_READY_TIMEOUT_MS);
      return () => window.clearTimeout(timer);
    };
    t12 = [isReadyToMount, handleFatal];
    $[10] = isReadyToMount;
    $[11] = t11;
    $[12] = t12;
  } else {
    t11 = $[11];
    t12 = $[12];
  }
  useEffect(t11, t12);
  const rendererRef = useRef(null);
  let t13;
  if ($[13] !== handleRendererReady) {
    t13 = defaultProps => {
      const canvas = defaultProps.canvas as HTMLCanvasElement;
      const cached = rendererRef.current;
      if (cached?.canvas === canvas) {
        return cached.renderer;
      }
      const pending = createRenderer(canvas, handleFatal, handleRendererReady);
      rendererRef.current = {
        canvas,
        renderer: pending
      };
      return pending;
    };
    $[13] = handleRendererReady;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const glFactory = t13;
  if (!isReadyToMount) {
    return null;
  }
  const t14 = paused ? "never" : "always";
  let t15;
  if ($[15] !== cameraFov || $[16] !== cameraPosition) {
    t15 = {
      position: cameraPosition,
      fov: cameraFov
    };
    $[15] = cameraFov;
    $[16] = cameraPosition;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      scroll: false,
      debounce: {
        scroll: 50,
        resize: 0
      }
    };
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <RendererSizeSync />;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  let t19;
  if ($[20] !== paused) {
    t18 = <RenderWatchdog onFatalError={handleFatal} paused={paused} />;
    t19 = !paused && <PerformanceMonitor onIncline={() => setDpr(MAX_DPR)} onDecline={() => setDpr(MIN_DPR)} />;
    $[20] = paused;
    $[21] = t18;
    $[22] = t19;
  } else {
    t18 = $[21];
    t19 = $[22];
  }
  let t20;
  if ($[23] !== onShaderCompileError || $[24] !== onShaderCompiled || $[25] !== readyToCompile) {
    t20 = !!readyToCompile && <ShaderPrecompiler readyToCompile={readyToCompile} onCompiled={onShaderCompiled} onError={onShaderCompileError} />;
    $[23] = onShaderCompileError;
    $[24] = onShaderCompiled;
    $[25] = readyToCompile;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== children || $[28] !== dpr || $[29] !== glFactory || $[30] !== t14 || $[31] !== t15 || $[32] !== t18 || $[33] !== t19 || $[34] !== t20) {
    t21 = <Canvas frameloop={t14} gl={glFactory} camera={t15} dpr={dpr} resize={t16} className="absolute inset-0 size-full">{t17}{t18}{t19}{t20}{children}</Canvas>;
    $[27] = children;
    $[28] = dpr;
    $[29] = glFactory;
    $[30] = t14;
    $[31] = t15;
    $[32] = t18;
    $[33] = t19;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  return t21;
};
function _temp(state) {
  return state.gl;
}
function _temp2(state_0) {
  return state_0.size;
}
function _temp3(state) {
  return state.gl;
}
function _temp4(state_0) {
  return state_0.size;
}
function _temp5(state_1) {
  return state_1.invalidate;
}